import { useNavigate } from '@tanstack/react-router';
import { Command } from 'cmdk';

import { translate } from '../../helpers/translate.helper';
import { useGroupContext } from '../../hooks/useGroupContext';
import { useRole } from '../../hooks/useRole';
import { IconArrowNarrowRight } from '../../icons/cdl/ArrowNarrowRight';
import {
    getGroupContextCompanySearch,
    getGroupContextCustomerCompanySearch,
    getGroupContextCustomerDemandSearch,
    getGroupContextCustomerGroupSearch,
} from '../../utils/getGroupContextSearchParams';
import { useShortcutMenu } from '../hooks/useShortcutMenu';

import { CommandEmptyState } from './CommandEmptyState';
import { CommandItem } from './CommandItem';

interface NavigateItem {
    key: string;
    label: string;
    onSelect: () => void;
}

export const NavigateTo = () => {
    const navigate = useNavigate();
    const role = useRole();
    const groupContext = useGroupContext();
    const { setOpen } = useShortcutMenu();

    const companySearch = getGroupContextCompanySearch(groupContext);
    const customerCompanySearch = getGroupContextCustomerCompanySearch(groupContext);
    const customerGroupSearch = getGroupContextCustomerGroupSearch(groupContext);
    const customerDemandSearch = getGroupContextCustomerDemandSearch(groupContext);

    const getNavigateItems = (): NavigateItem[] => {
        if (role.isSuperAdmin()) {
            return [
                {
                    key: 'dashboard',
                    label: translate('navigation.dashboard'),
                    onSelect: () => navigate({ to: '/dashboard' }),
                },
                {
                    key: 'orders',
                    label: translate('navigation.orders'),
                    onSelect: () => navigate({ to: '/orders', search: companySearch }),
                },
                {
                    key: 'offers',
                    label: translate('navigation.offers'),
                    onSelect: () => navigate({ to: '/offers', search: companySearch }),
                },
                {
                    key: 'requests-for-quotes',
                    label: translate('navigation.requestsForQuotes'),
                    onSelect: () =>
                        navigate({
                            to: '/requests-for-quotes',
                            search: companySearch,
                        }),
                },
                {
                    key: 'key-ports',
                    label: translate('navigation.keyPorts'),
                    onSelect: () => navigate({ to: '/key-ports', search: customerGroupSearch }),
                },
                {
                    key: 'demand',
                    label: translate('navigation.demandOverview'),
                    onSelect: () => navigate({ to: '/demand', search: customerDemandSearch }),
                },
                {
                    key: 'statistics',
                    label: translate('navigation.reporting'),
                    onSelect: () => navigate({ to: '/statistics', search: companySearch }),
                },
                {
                    key: 'compare',
                    label: translate('navigation.compare'),
                    onSelect: () => navigate({ to: '/compare' }),
                },
                {
                    key: 'products',
                    label: translate('navigation.products'),
                    onSelect: () => navigate({ to: '/products' }),
                },
                {
                    key: 'contracts',
                    label: translate('navigation.contracts'),
                    onSelect: () => navigate({ to: '/contracts' }),
                },
                {
                    key: 'integrations',
                    label: translate('navigation.integrations'),
                    onSelect: () => navigate({ to: '/integrations' }),
                },
                {
                    key: 'vessels',
                    label: translate('navigation.vessels'),
                    onSelect: () => navigate({ to: '/vessels', search: customerCompanySearch }),
                },
                {
                    key: 'customer-groups',
                    label: translate('navigation.customers'),
                    onSelect: () => navigate({ to: '/customer-groups' }),
                },
                {
                    key: 'supplier-groups',
                    label: translate('navigation.suppliers'),
                    onSelect: () => navigate({ to: '/supplier-groups' }),
                },
                {
                    key: 'agents',
                    label: translate('navigation.agents'),
                    onSelect: () => navigate({ to: '/agents', search: customerCompanySearch }),
                },
                {
                    key: 'assignments',
                    label: translate('navigation.assignments'),
                    onSelect: () => navigate({ to: '/assignments', search: companySearch }),
                },
                {
                    key: 'ports',
                    label: translate('navigation.ports'),
                    onSelect: () => navigate({ to: '/ports' }),
                },
                {
                    key: 'users',
                    label: translate('navigation.users'),
                    onSelect: () => navigate({ to: '/members' }),
                },
            ];
        }

        if (role.isCustomer()) {
            return [
                {
                    key: 'dashboard',
                    label: translate('navigation.dashboard'),
                    onSelect: () => navigate({ to: '/dashboard' }),
                },
                {
                    key: 'orders',
                    label: translate('navigation.orders'),
                    onSelect: () => navigate({ to: '/orders' }),
                },
                {
                    key: 'demand',
                    label: translate('navigation.demandOverview'),
                    onSelect: () => navigate({ to: '/demand' }),
                },
                {
                    key: 'compare',
                    label: translate('navigation.compare'),
                    onSelect: () => navigate({ to: '/compare' }),
                },
                {
                    key: 'vessels',
                    label: translate('navigation.vessels'),
                    onSelect: () => navigate({ to: '/vessels' }),
                },
                {
                    key: 'key-ports',
                    label: translate('navigation.keyPorts'),
                    onSelect: () => navigate({ to: '/key-ports' }),
                },
                {
                    key: 'members',
                    label: translate('navigation.users'),
                    onSelect: () =>
                        navigate({ to: '/customer-groups/$groupId/members', params: { groupId: role.group.id } }),
                },
                ...(role.hasAdminRights()
                    ? [
                          {
                              key: 'group-management',
                              label: translate('settings.groupManagement.title'),
                              onSelect: () =>
                                  navigate({ to: '/customer-groups/$groupId', params: { groupId: role.group.id } }),
                          },
                      ]
                    : []),
                ...(!role.hasAdminRights() && role.hasAnyAdminRights()
                    ? [
                          {
                              key: 'company-management',
                              label: translate('settings.companyManagement.title'),
                              onSelect: () =>
                                  navigate({
                                      to: '/customer-groups/$groupId/companies',
                                      params: { groupId: role.group.id },
                                  }),
                          },
                      ]
                    : []),
                ...(role.hasAnyAdminRights()
                    ? [
                          {
                              key: 'contracts',
                              label: translate('navigation.contracts'),
                              onSelect: () => navigate({ to: '/contracts' }),
                          },
                      ]
                    : []),
                ...(role.hasAnyAdminRights()
                    ? [
                          {
                              key: 'agents',
                              label: translate('navigation.agents'),
                              onSelect: () => navigate({ to: '/agents' }),
                          },
                      ]
                    : []),
                {
                    key: 'statistics',
                    label: translate('navigation.reporting'),
                    onSelect: () => navigate({ to: '/statistics' }),
                },
            ];
        }

        if (role.isSupplier()) {
            return [
                {
                    key: 'dashboard',
                    label: translate('navigation.dashboard'),
                    onSelect: () => navigate({ to: '/dashboard' }),
                },
                {
                    key: 'offers',
                    label: translate('navigation.orders'),
                    onSelect: () => navigate({ to: '/offers' }),
                },
                ...(role.hasAnyAdminRights()
                    ? [
                          {
                              key: 'customers',
                              label: translate('navigation.customers'),
                              onSelect: () => navigate({ to: '/customers' }),
                          },
                      ]
                    : []),
                {
                    key: 'statistics',
                    label: translate('navigation.reporting'),
                    onSelect: () => navigate({ to: '/statistics' }),
                },
            ];
        }

        return [];
    };

    const navigateItems = getNavigateItems();

    if (!navigateItems.length) {
        return <CommandEmptyState />;
    }

    return (
        <Command.List>
            <Command.Empty>
                <CommandEmptyState />
            </Command.Empty>
            {navigateItems.map((item) => (
                <CommandItem
                    key={item.key}
                    value={item.label}
                    Icon={IconArrowNarrowRight}
                    iconVisibility="selected"
                    onSelect={() => {
                        item.onSelect();
                        setOpen(false);
                    }}
                >
                    {item.label}
                </CommandItem>
            ))}
        </Command.List>
    );
};
